/**
 * <ToothChart> — the interactive arch-position picker (odontogram).
 *
 * One implementation for every app. Before this, apps/main and apps/portal
 * each carried their own picker AND their own ~900-line copy of the SVG body,
 * while `ToothChartSvg` — the same graphic, with its geometry already lifted
 * out into toothchart-geometry.json — sat here unused. Three copies of one
 * drawing, and the two pickers had already drifted (different quick-select
 * rows, one wrapping strip of 16 vs two rows of 8), so a doctor and a lab
 * tech saw different charts for the same field.
 *
 * Modes:
 *   readonly     diagram only, selected positions highlighted
 *   interactive  diagram + numbered grid, per-position toggling
 *   arch-only    diagram + Upper / Lower (jaw_chart) — the value is still a
 *                whole arch's worth of numbers, never one position
 *
 * Positions are Universal Numbering 1-32 (1-16 upper, 17-32 lower), the same
 * `string[]` the tooth_chart / jaw_chart intake fields read and write.
 *
 * ENG-1150 — per-unit selection caps. `maxSelected` bounds how many the picker
 * lets a single unit choose (interactive: teeth; arch-only: arches). At the cap
 * the "bulk" quick-selects that would blow past it are gone: interactive drops
 * "All teeth" and the per-jaw "Select all"; arch-only drops "Both" unless the
 * cap is >= 2 arches. Ordering a whole mouth of crowns is what adding UNITS is
 * for, not one over-stuffed picker. `readonly` is untouched — read views still
 * collapse a historical whole-mouth answer to "Both".
 */
import type { JSX } from 'react';
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from '@clearpath/design-system/ui/button';
import { cn } from '@clearpath/design-system/lib/utils';
import { ToothChartSvg } from "./ToothChartSvg.js";

export interface ToothChartProps {
  selectedTeeth: string[];
  onChange?: (next: string[]) => void;
  mode: "readonly" | "interactive" | "arch-only";
  /**
   * Whole-arch reading (`jaw_chart`). A jaw_chart value is stored as a whole
   * arch's worth of positions — the exact same `string[]` a per-position
   * tooth_chart uses — so without this the read view says "32 selected: #1,
   * #2, …" for what the picker presented as a single Upper / Lower / Both
   * choice (ENG-909). A partial or mixed set falls back to listing positions.
   */
  archMode?: boolean;
  /**
   * Per-unit selection cap (ENG-1150). `interactive`: max teeth. `arch-only`:
   * max ARCHES (1 ⇒ Upper xor Lower, no "Both"; >= 2 ⇒ "Both" returns).
   * Undefined ⇒ uncapped (legacy behavior). A cap of <= 0 is treated as
   * uncapped rather than locking the picker.
   */
  maxSelected?: number;
  /** `readonly` only: hides the caption under the diagram. Default true. */
  showCaption?: boolean;
  "aria-label"?: string;
  className?: string;
  /** SVG figure size in px (square). Default 200. */
  svgSize?: number;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

export const UPPER_RANGE = Array.from({ length: 16 }, (_, i) => String(i + 1));
export const LOWER_RANGE = Array.from({ length: 16 }, (_, i) => String(i + 17));

/**
 * archLabel — collapse a whole-arch position set to its human label.
 *
 *   - all 16 upper positions and nothing else -> "Upper"
 *   - all 16 lower positions and nothing else -> "Lower"
 *   - the full 32                             -> "Both"
 *
 * Anything else (a partial or mixed set) returns null, so the caller falls
 * back to listing the individual positions.
 */
export function archLabel(
  selected: readonly (string | number)[],
): "Upper" | "Lower" | "Both" | null {
  const positions = new Set(
    selected
      .filter((p) => p !== null && p !== undefined && `${p}` !== "")
      .map((p) => `${p}`),
  );
  const allUpper = UPPER_RANGE.every((p) => positions.has(p));
  const allLower = LOWER_RANGE.every((p) => positions.has(p));
  if (allUpper && allLower && positions.size === 32) return "Both";
  if (allUpper && positions.size === 16) return "Upper";
  if (allLower && positions.size === 16) return "Lower";
  return null;
}

/**
 * Selected-position fill. Hard-coded rather than `var(--accent)`: the SVG
 * ships every path with a baked-in `fill="white"` baseline, and a muted
 * dark-mode accent reads poorly against it. Deselect restores "white" — the
 * baseline — rather than transparent, which used to expose the dark panel
 * beneath and left deselected positions looking stuck-dark.
 */
const SELECTED_FILL = "#3b82f6";

export function ToothChart({
  selectedTeeth,
  onChange,
  mode,
  className,
  svgSize = 200,
  archMode = false,
  maxSelected,
  showCaption = true,
  ...aria
}: ToothChartProps): JSX.Element {
  // A cap only constrains when it's a positive finite number; anything else
  // (undefined, 0, NaN) means "uncapped" so the picker never locks itself.
  const toothCap =
    typeof maxSelected === "number" && Number.isFinite(maxSelected) && maxSelected > 0
      ? Math.floor(maxSelected)
      : undefined;
  // arch-only counts in arches; "Both" (2 arches) is only offered when the cap
  // allows two. Default single-arch when uncapped is intentional (ENG-1150).
  const allowBothArches = toothCap === undefined ? false : toothCap >= 2;
  // Mirror the value locally so a click paints before the controlling form's
  // state cycles back; resync when the prop changes.
  const [selection, setSelection] = useState<string[]>(selectedTeeth);
  useEffect(() => setSelection(selectedTeeth), [selectedTeeth]);

  const previousRef = useRef<string[]>([]);
  const svgRef = useRef<SVGSVGElement | null>(null);

  useEffect(() => {
    const fill = svgRef.current?.querySelector("g#Fill");
    if (!fill) return;
    const nextIds = selection.map((n) => `T${n}`);
    for (const prevId of previousRef.current) {
      if (!nextIds.includes(prevId)) {
        fill.querySelector(`path[id="${prevId}"]`)?.setAttribute("fill", "white");
      }
    }
    fill.querySelectorAll("path").forEach((path) => {
      const id = path.getAttribute("id");
      if (id && nextIds.includes(id)) path.setAttribute("fill", SELECTED_FILL);
    });
    previousRef.current = nextIds;
  }, [selection]);

  const emit = (next: string[]) => {
    setSelection(next);
    onChange?.(next);
  };

  const atCap = toothCap !== undefined && selection.length >= toothCap;
  const toggle = (tooth: string) => {
    if (mode !== "interactive") return;
    const isSelected = selection.includes(tooth);
    // Deselect is always allowed; a new selection is blocked once the per-unit
    // cap is reached (the button is also disabled, this is the guard).
    if (!isSelected && atCap) return;
    emit(isSelected ? selection.filter((t) => t !== tooth) : [...selection, tooth]);
  };

  const allUpper = useMemo(
    () => UPPER_RANGE.every((t) => selection.includes(t)),
    [selection],
  );
  const allLower = useMemo(
    () => LOWER_RANGE.every((t) => selection.includes(t)),
    [selection],
  );
  const allSelected = allUpper && allLower;

  const clearAll = () => emit([]);

  // arch-only replaces the selection outright — it is never additive the way
  // the interactive per-jaw toggles are.
  const selectArch = (kind: "upper" | "lower" | "both") =>
    emit(
      kind === "upper"
        ? UPPER_RANGE
        : kind === "lower"
          ? LOWER_RANGE
          : [...UPPER_RANGE, ...LOWER_RANGE],
    );

  const figure = (
    <div className="cp-toothchart-figure" style={{ width: svgSize, height: svgSize }}>
      <ToothChartSvg svgRef={svgRef} />
    </div>
  );

  if (mode === "readonly") {
    // The caption is what makes the diagram readable as data: it names the
    // positions (or the arch) so a reader is not left counting highlighted
    // teeth, and it is the only form the numbers take on a read surface.
    const sorted = [...selection]
      .filter((p) => p !== null && p !== undefined && `${p}` !== "")
      .map((p) => `${p}`)
      .sort((a, b) => Number(a) - Number(b));
    const arch = archMode ? archLabel(sorted) : null;

    return (
      <div
        className={["cp-toothchart cp-odontogram", className]
          .filter(Boolean)
          .join(" ")}
        role="group"
        aria-label={aria["aria-labelledby"] ? undefined : (aria["aria-label"] ?? "Selected positions")}
        aria-labelledby={aria["aria-labelledby"]}
      >
        {figure}
        {showCaption && (
          <p
            className="cp-odontogram-caption"
            data-selected-count={sorted.length}
            data-selection-label={arch ?? undefined}
          >
            {arch !== null ? (
              <span className="font-semibold">{arch}</span>
            ) : (
              <>
                {sorted.length > 0 && (
                  <span className="font-semibold">{sorted.length} selected: </span>
                )}
                {sorted.length === 0
                  ? "None selected"
                  : sorted.map((p) => `#${p}`).join(", ")}
              </>
            )}
          </p>
        )}
      </div>
    );
  }

  const groupProps = {
    role: "group" as const,
    "aria-labelledby": aria["aria-labelledby"],
    "aria-describedby": aria["aria-describedby"],
    "aria-invalid": aria["aria-invalid"],
    "aria-label": aria["aria-labelledby"] ? undefined : "Tooth selection",
  };

  if (mode === "arch-only") {
    return (
      // Same two-column arrangement as the interactive mode: figure left,
      // controls right, stacking under `sm`.
      <div
        {...groupProps}
        className={[
          "cp-toothchart flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6",
          className,
        ]
          .filter(Boolean)
          .join(" ")}
      >
        <div className="shrink-0">{figure}</div>
        <div className="cp-toothchart-controls cp-toothchart-controls-inline flex min-w-0 flex-1 flex-wrap items-center gap-2">
          <span className="cp-toothchart-hint">Select</span>
          <QuickBtn active={allUpper && !allLower} onClick={() => selectArch("upper")}>
            Upper
          </QuickBtn>
          <QuickBtn active={allLower && !allUpper} onClick={() => selectArch("lower")}>
            Lower
          </QuickBtn>
          {allowBothArches && (
            <QuickBtn active={allSelected} onClick={() => selectArch("both")}>
              Both
            </QuickBtn>
          )}
          <QuickBtn active={false} onClick={clearAll} disabled={selection.length === 0}>
            Clear
          </QuickBtn>
        </div>
      </div>
    );
  }

  return (
    // Figure beside the controls rather than above them: stacked, the chart
    // pushed Quick select and both jaws below the fold on a narrow form
    // column. Falls back to stacked under `sm`, where there is no room for
    // two columns.
    <div
      {...groupProps}
      className={[
        "cp-toothchart flex flex-col gap-4 sm:flex-row sm:items-start sm:gap-6",
        className,
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <div className="shrink-0">{figure}</div>
      <div className="cp-toothchart-controls flex min-w-0 flex-1 flex-col gap-3">
        <div className="cp-toothchart-quick flex flex-wrap items-center gap-2">
          {/* "All teeth" and the per-jaw "Select all" are gone (ENG-1150) — a
              per-unit cap makes selecting a whole arch/mouth for one unit
              wrong; order more UNITS instead. Clear stays. */}
          <QuickBtn active={false} onClick={clearAll} disabled={selection.length === 0}>
            Clear
          </QuickBtn>
          {(selection.length > 0 || toothCap !== undefined) && (
            <span className="cp-toothchart-count" aria-live="polite">
              {selection.length}
              {toothCap !== undefined ? ` of ${toothCap}` : ""} selected
            </span>
          )}
        </div>
        <ToothJaw
          label="Upper Jaw"
          teeth={UPPER_RANGE}
          selected={selection}
          atCap={atCap}
          onToggle={toggle}
        />
        <ToothJaw
          label="Lower Jaw"
          teeth={LOWER_RANGE}
          selected={selection}
          atCap={atCap}
          onToggle={toggle}
        />
      </div>
    </div>
  );
}

/** One jaw: a label, then positions in two rows of 8. The per-jaw "Select all"
 *  toggle is gone (ENG-1150) — bulk-selecting an arch defeats the per-unit cap.
 *  At the cap, unselected positions are disabled so the guard is visible, not
 *  just enforced on click. */
function ToothJaw({
  label,
  teeth,
  selected,
  atCap,
  onToggle,
}: {
  label: string;
  teeth: readonly string[];
  selected: readonly string[];
  atCap: boolean;
  onToggle: (t: string) => void;
}) {
  const half = Math.ceil(teeth.length / 2);
  return (
    <div className="cp-toothchart-jaw space-y-1.5">
      <div className="cp-toothchart-jaw-head flex items-center gap-2">
        <span className="cp-toothchart-hint">{label}</span>
      </div>
      {[teeth.slice(0, half), teeth.slice(half)].map((row, index) => (
        <div key={index} className="cp-toothchart-row flex flex-wrap gap-1">
          {row.map((n) => {
            const isSelected = selected.includes(n);
            return (
              // A raw <button> here carried no styling at all — cp-tooth has no
              // CSS anywhere — so once preflight stripped the browser's default
              // chrome a row of teeth read as one run of digits. The shared
              // Button gives it real affordance; the className only makes it
              // compact enough for 16 across.
              <Button
                key={n}
                type="button"
                variant={isSelected ? "default" : "outline"}
                onClick={() => onToggle(n)}
                // Only unselected teeth lock at the cap — a selected one must
                // still be clickable to deselect.
                disabled={atCap && !isSelected}
                aria-pressed={isSelected}
                aria-label={`Tooth ${n}`}
                className={cn(
                  "cp-tooth h-7 min-w-7 px-1.5 font-mono text-xs tabular-nums",
                  isSelected && "cp-tooth-selected",
                )}
              >
                {n}
              </Button>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function QuickBtn({
  active,
  onClick,
  disabled,
  children,
}: {
  active: boolean;
  onClick: () => void;
  disabled?: boolean;
  children: React.ReactNode;
}) {
  return (
    <Button
      type="button"
      variant={active ? "default" : "outline"}
      size="sm"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={active}
    >
      {children}
    </Button>
  );
}
